import { Injectable, inject, signal } from '@angular/core';
import { Bm25Service } from './bm25.service';
import { EmbeddingService } from './embedding.service';
import { KeywordExtractionService } from './keyword-extraction.service';
import { RagPersistenceService } from './rag-persistence.service';
import { IndexingStatus, RagChunk, RagDocument, RagSearchResult, RetrievalStrategy } from '../models/rag.models';

@Injectable({ providedIn: 'root' })
export class RagService {
  private readonly bm25 = inject(Bm25Service);
  private readonly embeddings = inject(EmbeddingService);
  private readonly keywordExtraction = inject(KeywordExtractionService);
  private readonly persistence = inject(RagPersistenceService);
  private readonly documentStore = signal<RagDocument[]>([]);

  readonly documents = this.documentStore.asReadonly();
  readonly strategy = signal<RetrievalStrategy>('bm25');


  async loadDocuments(): Promise<void> {
    const stored = await this.persistence.loadAll();
    // Docs that were mid-indexing when the page closed are incomplete — mark them as error.
    const recovered = stored.map((doc) =>
      doc.status === 'indexing' ? { ...doc, status: 'error' as const, error: 'Indexing was interrupted. Please re-index.' } : doc
    );
    this.documentStore.set(recovered);
  }

  async addDocument(name: string, content: string, size = new TextEncoder().encode(content).length): Promise<RagDocument> {
    console.log('Adding document:', name, 'Size:', size, 'Content preview:', content.substring(0, 100) + '...');
    const document: RagDocument = {
      id: crypto.randomUUID(),
      name,
      size,
      content,
      chunks: [],
      status: 'indexing',
    };
    this.documentStore.update((documents) => [...documents, document]);
    await this.persistence.saveDocument(document);
    await this.indexDocument(document.id);
    return this.getDocument(document.id) ?? document;
  }

  removeDocument(documentId: string): void {
    this.documentStore.update((documents) => documents.filter((document) => document.id !== documentId));
    this.persistence.deleteDocument(documentId);
  }

  async indexDocument(documentId: string): Promise<void> {
    const document = this.getDocument(documentId);
    if (!document) return;
    console.log('Indexing document:', document.name);
    this.updateDocument(documentId, { status: 'indexing', error: undefined });

    try {
      console.log('Chunking document content...');
      // Limit number of chunks to avoid excessive processing on very large documents.
      let chunks = this.chunkDocument(document.content, documentId, document.name);
      const MAX_CHUNKS = 200;
      if (chunks.length > MAX_CHUNKS) {
        chunks = chunks.slice(0, MAX_CHUNKS);
      }
      console.log('Chunks created:', chunks.length);
      if (this.strategy() === 'vector') {
        console.log('Vector strategy - embedding chunks...');
        for (const chunk of chunks) chunk.embedding = await this.embeddings.embed(chunk.content);
      }
      console.log('Extracting keywords from document...');
      // Use KeywordExtractionService to extract top keywords from all chunks
      const extracted = this.keywordExtraction.extractKeywords(chunks, 50);
      const keywords = extracted.map(k => k.term);
      console.log('Keywords extracted:', keywords.length);
      console.log('Top keywords:', keywords.slice(0, 10));
      console.log('Updating document with chunks, keywords and status');
      this.updateDocument(documentId, { chunks, keywords, status: 'indexed' });
      const updated = this.getDocument(documentId);
      if (updated) await this.persistence.saveDocument(updated);
    } catch (error) {
      console.error('Indexing error:', error);
      this.updateDocument(documentId, { chunks: [], status: 'error', error: error instanceof Error ? error.message : 'Indexing failed.' });
      this.persistence.saveDocument(this.getDocument(documentId)!);
    }
  }

  chunkDocument(text: string, documentId = '', documentName = ''): RagChunk[] {
    console.log('Chunking document:', documentName, 'Text length:', text.length);
    const normalized = text.trim();
    if (!normalized) return [];
    // Reduce chunk size for very large documents to keep processing fast.
    let chunkSize = 700;
    if (text.length > 200000) {
      chunkSize = 400; // smaller chunks for huge docs
    }
    const overlap = 100;
    const chunks: RagChunk[] = [];
    let start = 0;
    let chunkIndex = 0;

    while (start < normalized.length) {
      let end = Math.min(start + chunkSize, normalized.length);
      if (end < normalized.length) {
        const boundary = normalized.lastIndexOf(' ', end);
        if (boundary > start + overlap) end = boundary;
      }
      const content = normalized.slice(start, end).trim();
      if (content) chunks.push({ id: `${documentId}-chunk-${chunkIndex}`, documentId, documentName, content, chunkIndex });
      if (end >= normalized.length) break;
      start = Math.max(end - overlap, start + 1);
      chunkIndex++;
    }
    console.log('Created', chunks.length, 'chunks');
    return chunks;
  }

  async search(query: string, topK = 5, strategy: RetrievalStrategy = this.strategy()): Promise<RagSearchResult[]> {
    return strategy === 'bm25' ? this.searchBm25(query, topK) : this.searchVector(query, topK);
  }

  searchBm25(query: string, topK = 5): RagSearchResult[] {
    return this.bm25.search(query, this.indexedChunks(), topK);
  }

  async searchVector(_query: string, _topK = 5): Promise<RagSearchResult[]> {
    await this.embeddings.embed(_query);
    return [];
  }

  buildContext(results: RagSearchResult[]): string {
    return results
      .map(({ chunk }) => `SOURCE: ${chunk.documentName}\n\n${chunk.content}`)
      .join('\n\n---\n\n');
  }

  private indexedChunks(): RagChunk[] {
    return this.documents().filter((document) => document.status === 'indexed').flatMap((document) => document.chunks);
  }

  private getDocument(documentId: string): RagDocument | undefined {
    return this.documents().find((document) => document.id === documentId);
  }

  private updateDocument(documentId: string, update: Partial<RagDocument>): void {
    this.documentStore.update((documents) => documents.map((document) => document.id === documentId ? { ...document, ...update } : document));
  }
}
